import { ENVS } from "./envs";

// Absolute URLs from env (no dev proxy)
const API_BASE = ENVS.backendUrl;

const path = (root: string, sublink: string) => {
  return `${API_BASE}${root}${sublink}`;
};

const ROOT = "";
const API_USER = "/users";
const API_AUTH = "/auth";
const API_PROJECT = "/projects";
const API_FILE_STORAGE = "/file-storage";

const API_PRICING_PLAN = "/pricing-plans";
const API_PAYMENT = "/payments";
const API_WORKSPACE = "/workspaces";

export const USER_API = {
  // User Management
  register: path(API_USER, "/register"),
  login: path(API_AUTH, "/login"),
  mfa: path(API_AUTH, "/mfa"),
  mfaCompleteLogin: path(API_AUTH, "/mfa/complete-login"),
  mfaCompleteSetup: path(API_AUTH, "/mfa/complete-setup"),
  refreshToken: path(API_AUTH, "/refresh-token"),
  logout: path(API_AUTH, "/logout"),
  resendEmail: path(API_USER, "/resend-verification"),
  me: path(API_USER, "/me"),
  catalog: path(API_USER, "/me/catalog"),
  preferences: path(API_USER, "/me/preferences"),
  profile: path(API_USER, "/profile"),
  initiateSsoLoginUrl: path(ROOT, "/oidc/initiate-login"),
  completeSsoLoginUrl: path(ROOT, "/oidc/login"),
  initiateSocialOAuth: (provider: "google" | "linkedin", referralCode?: string) => {
    const base = path(API_AUTH, `/oauth/${provider}/initiate`);
    const code = referralCode?.trim().toLowerCase();
    if (!code) {
      return base;
    }
    return `${base}?ref=${encodeURIComponent(code)}`;
  },
  completeSocialOAuth: (provider: "google" | "linkedin") =>
    path(API_AUTH, `/oauth/${provider}/callback`),
  oauthAvailability: path(API_AUTH, "/oauth/availability"),

  // Password Management
  forgotPassword: path(API_AUTH, "/forgot-password"),
  resetPassword: path(API_AUTH, "/reset-password"),
  passwordUpdate: path(API_AUTH, "/change-password"),

  // Notifications
  notifications: path(ROOT, "/notifications"),
  markAsRead: path(ROOT, "/notifications/read"),
  /** Marks every unread notification of the signed-in user as read. No body. */
  markAllAsRead: path(ROOT, "/notifications/read/all"),

  // Account Management
  disableAccount: path(API_USER, "/deactivate"),
  activateAccount: path(API_USER, "/activate"),
  deleteAccount: path(API_USER, "/delete"),
  cancelDeletion: path(API_USER, "/cancel-deletion"),
  resendCancelDeletionEmail: path(API_USER, "/resend-cancel-deletion-email"),

  // Email Change Management
  changeEmailInitiate: path(API_USER, "/change-email/initiate"),
  changeEmailVerify: path(API_USER, "/change-email/verify"),
  changeEmailResend: path(API_USER, "/change-email/resend"),
  changeEmailStatus: path(API_USER, "/change-email/status"),

  // Passkey Management
  passkeyRegisterBegin: path(API_AUTH, "/passkey/register/begin"),
  passkeyRegisterComplete: path(API_AUTH, "/passkey/register/complete"),
  passkeyAuthenticateBegin: path(API_AUTH, "/passkey/authenticate/begin"),
  passkeyAuthenticateComplete: path(API_AUTH, "/passkey/authenticate/complete"),
  passkeyCredentials: path(API_AUTH, "/passkey/credentials"),
  passkeyDeleteCredential: (id: string) => path(API_AUTH, `/passkey/credentials/${id}`),
  passkeyUpdateCredential: (id: string) => path(API_AUTH, `/passkey/credentials/${id}/name`),

  // Project Endpoint
  projects: path(API_PROJECT, ""),
  projectsSummary: (workspaceId: string) =>
    path(API_PROJECT, `/summary?workspace_id=${encodeURIComponent(workspaceId)}`),
  projectsWithQuery: (query: string) => `${path(API_PROJECT, "")}${query ? `?${query}` : ""}`,
  getProjectWithQuery: (projectId: string, query: string) =>
    `${path(API_PROJECT, `/${projectId}`)}${query ? `?${query}` : ""}`,
  getProjectVersions: (projectId: string) => path(API_PROJECT, `/${projectId}/versions`),
  getVersionSettings: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/versions/${documentId}/settings`),
  patchVersionSettings: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/versions/${documentId}/settings`),
  activateVersion: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/versions/${documentId}/activate`),
  createDocument: path(ROOT, "/documents"),
  createProjectVersion: (projectId: string) => path(API_PROJECT, `/${projectId}/versions`),
  getProjectQuota: path(API_PROJECT, "/quotas"),
  parseFile: path(API_PROJECT, "/parse"),
  // Async project import jobs (server-side parse + create)
  temporaryUploadUrl: path(API_FILE_STORAGE, "/temporary-upload-url"),
  // Signed image upload URLs (the backend picks the storage path)
  profileImageUploadUrl: path(API_FILE_STORAGE, "/users/image-upload-url"),
  workspaceImageUploadUrl: (workspaceId: string) =>
    path(API_FILE_STORAGE, `/workspaces/${workspaceId}/image-upload-url`),
  importJobs: path(API_PROJECT, "/import-jobs"),
  importJob: (jobId: string) => path(API_PROJECT, `/import-jobs/${jobId}`),
  projectOwnerCompany: path(API_USER, "/host-organizations"),
  archiveProject: (projectId: string) => path(API_PROJECT, `/${projectId}/archive`),
  unarchiveProject: (projectId: string) => path(API_PROJECT, `/${projectId}/unarchive`),
  deleteProject: (projectId: string) => path(API_PROJECT, `/${projectId}`),
  getProject: (projectId: string) => path(API_PROJECT, `/${projectId}`),
  getProjectTeamRole: (projectId: string) => path(API_PROJECT, `/${projectId}/team-role`),
  getProjectCollaborators: (projectId: string) => path(API_PROJECT, `/${projectId}/collaborators`),

  // Enhanced Project Management
  createProject: path(API_PROJECT, ""), // POST - Create new project
  addDemoProject: path(API_PROJECT, "/demo/add"),
  updateProject: (projectId: string) => path(API_PROJECT, `/${projectId}`), // PATCH - Update project

  // Project Search and Filtering
  searchProjects: path(API_PROJECT, "/search"), // GET - Search projects
  getProjectsByType: (type: string) => path(API_PROJECT, `/type/${type}`), // GET - Filter by type

  // Project Analytics
  getProjectStats: (projectId: string) => path(API_PROJECT, `/${projectId}/stats`), // GET - Project statistics
  getProjectActivity: (projectId: string) => path(API_PROJECT, `/${projectId}/activity`), // GET - Project activity log

  // Pricing Plan Endpoints
  pricingPlans: path(API_PRICING_PLAN, ""),
  buySeats: path(API_PAYMENT, "/purchase-seats"),
  buyProjects: path(API_PAYMENT, "/purchase-projects"),
  getBillingPortalUrl: path(API_PAYMENT, "/billing-portal-url"),
  purchasePlan: (planId: string) => path(API_PRICING_PLAN, `/${planId}/purchase`),
  downgradePlan: (planId: string) => path(API_PRICING_PLAN, `/downgrade/${planId}`),
  getSubscriptionUpgradeConfirmUrl: (planId: string) =>
    path(API_PRICING_PLAN, `/subscription-upgrade-confirm-url/${planId}`),
  downgradeSeats: path(API_PAYMENT, "/downgrade-seats"),
  downgradeProjects: path(API_PAYMENT, "/downgrade-projects"),
  cancelPlanSubscription: path(API_PRICING_PLAN, "/cancel-subscription"),
  reactivateSubscription: path(API_PRICING_PLAN, "/reactivate-subscription"),

  cancelDowngrade: path(API_PRICING_PLAN, "/cancel-downgrade"),

  // Project Endpoints
  createProjectTrial: path(API_PAYMENT, "/one-time-trial"),
  createProjectPurchase: path(API_PAYMENT, "/one-time-purchase"),
  confirmOneTimePurchase: path(API_PAYMENT, "/one-time-purchase/confirm"),
  otpProducts: path(API_PAYMENT, "/otp-products"),
  upgradeWriterToStudioLicense: path(API_PAYMENT, "/upgrade-license"),

  // Screenplay Endpoints
  createScript: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/breakdown`),
  getAllScenes: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scenes`),
  getScene: (projectId: string, documentId: string, sceneId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scenes/${sceneId}`),
  getDraftScenes: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/scenes`),
  /** Short-lived ticket that opens one scene's live editing room. */
  createCollabTicket: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/collab-tickets`),
  createDraftScene: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/scenes`),
  deleteDraftScene: (
    projectId: string,
    documentId: string,
    sceneId: string,
    baseRevision?: number
  ) => {
    const base = path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/draft/scenes/${sceneId}`
    );
    if (baseRevision === undefined) return base;
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}base_revision=${String(baseRevision)}`;
  },
  getDraftScene: (projectId: string, documentId: string, sceneId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/scenes/${sceneId}`),
  getDraftCharacterNames: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/character-names`),
  updateDraftScene: (projectId: string, documentId: string, sceneId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/scenes/${sceneId}`),
  reorderDraftScenes: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/scenes/reorder`),
  listScriptRevisions: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/revisions`),
  getCurrentScriptRevision: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/revisions/current`),
  lockScriptRevision: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/revisions/lock`),
  /** Whole-script revision mark status against the latest locked pass (same rule as the PDF). */
  getScriptRevisionMarks: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/revision-marks`),
  lockScriptDocument: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/document/lock`),
  unlockScriptDocument: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/document/unlock`),
  listScriptShareLinks: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/share-links`),
  createScriptShareLink: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/share-links`),
  updateScriptShareLink: (projectId: string, documentId: string, shareLinkId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/share-links/${shareLinkId}`),
  revokeScriptShareLink: (projectId: string, documentId: string, shareLinkId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/share-links/${shareLinkId}`),
  restoreScriptShareLink: (projectId: string, documentId: string, shareLinkId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/draft/share-links/${shareLinkId}/restore`
    ),
  permanentlyDeleteScriptShareLink: (projectId: string, documentId: string, shareLinkId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/draft/share-links/${shareLinkId}/permanent`
    ),
  getSharedScript: (token: string) => path(ROOT, `/share/scripts/${token}`),
  getDraftSnapshots: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/snapshots`),
  getDraftSnapshot: (projectId: string, documentId: string, snapshotId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/snapshots/${snapshotId}`),
  createDraftSnapshot: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/snapshots`),
  patchDraftDocument: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/document`),
  getDraftSnapshotDiff: (projectId: string, documentId: string, snapshotId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/snapshots/${snapshotId}/diff`),
  restoreDraftSnapshot: (projectId: string, documentId: string, snapshotId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/draft/snapshots/${snapshotId}/restore`
    ),
  getDraftSnapshotSceneDiff: (
    projectId: string,
    documentId: string,
    snapshotId: string,
    sceneId: string | number
  ) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/draft/snapshots/${snapshotId}/scenes/${sceneId}/diff`
    ),
  applyDraftTag: (projectId: string, documentId: string, sceneId: string | number) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/scenes/${sceneId}/tags`),
  bulkRetagDraftTagDefinition: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/draft/tags/bulk-retag-definition`),
  getDocumentCommentsSummary: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/comments/summary`),
  getDocumentComments: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/comments`),
  /** `{ data: { thread_count, open_thread_count } }` for one document. */
  getDocumentCommentCount: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/comments/count`),
  createDocumentComment: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/comments`),
  resolveDocumentComment: (projectId: string, documentId: string, commentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/comments/${commentId}/resolve`),
  reopenDocumentComment: (projectId: string, documentId: string, commentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/comments/${commentId}/reopen`),
  /** PATCH body `{ body: string }`; DELETE removes comment (thread roots delete replies). */
  documentCommentById: (projectId: string, documentId: string, commentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/comments/${commentId}`),
  getDocumentCommentThreadReads: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/comments/comment-thread-reads`),
  /** PUT marks read; DELETE clears read cursor (thread may show as unread again). */
  documentCommentThreadReadForRoot: (
    projectId: string,
    documentId: string,
    rootCommentId: string
  ) =>
    path(
      API_PROJECT,
      `/${projectId}/documents/${documentId}/comments/comment-thread-reads/${rootCommentId}`
    ),
  getCharacters: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/character-breakdowns/characters`),
  getCharactersWithDisplayNames: (projectId: string, documentId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/character-breakdowns/characters/display-names`
    ),
  getAllCharactersDetailed: (projectId: string, documentId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/character-breakdowns/characters/detailed`
    ),
  getSingleCharacter: (projectId: string, documentId: string, characterId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/character-breakdowns/characters/${characterId}`
    ),
  getCharacterImageUploadUrl: (projectId: string, documentId: string) =>
    path(
      API_FILE_STORAGE,
      `/projects/${projectId}/screenplays/${documentId}/character-breakdowns/image-upload-url`
    ),
  getCharacterImage: (projectId: string, documentId: string, fileName: string) =>
    path(
      API_FILE_STORAGE,
      `/projects/${projectId}/screenplays/${documentId}/cast-data/images/${fileName}`
    ),
  getCharSummary: (projectId: string, documentId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/character-breakdowns/characters/summary`
    ),
  getOrPatchCoverage: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/coverage`),
  getExportPdf: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/export-pdf`),
  getExportFdx: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/export-fdx`),
  getExportFountain: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/export-fountain`),
  getExportMarkdown: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/export-markdown`),
  getExportDocx: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/export-docx`),
  getExportOmc: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/omc/export`),
  getExport: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/export`),
  trackExport: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/exports/track`),
  regeneratePartOfCoverageSummary: (projectId: string, documentId: string | undefined) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/coverage/generateNewSummaryPart`),
  regenerateCoverageSummary: (projectId: string, documentId: string | undefined) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/coverage/generateNewSummary`),
  updateCoverageSummary: (projectId: string, documentId: string | undefined) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/coverage/updateCoverageSummary`),
  regeneratePartOfCoverageComments: (projectId: string, documentId: string | undefined) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/coverage/generateNewCommentsPart`),
  regenerateCoverageComments: (projectId: string, documentId: string | undefined) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/coverage/generateNewComments`),
  updateCoverageComments: (projectId: string, documentId: string | undefined) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/coverage/updateCoverageComments`),

  // Tag Resources
  tags: path(ROOT, "/tag-categories"),
  addOrRemoveTag: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/tag`),
  editTagData: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/tag-display-name`),
  standAloneTag: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/standalone-tags`),
  getTagReport: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/tags-report`),
  getTagCategories: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/tag-categories`),
  getScenesReport: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scenes-report`),
  deleteTags: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/tags`),
  mergeTags: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/merge-tags`),
  unmergeTags: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/unmerge-tags`),

  // Note Endpoints
  getOrAddNote: (projectId: string, documentId: string, sceneId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scenes/${sceneId}/notes`),
  updateNote: (projectId: string, documentId: string, sceneId: string, noteId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scenes/${sceneId}/notes/${noteId}`),
  editTag: (projectId: string, documentId: string, tagCategoryId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/tag-categories/${tagCategoryId}`),

  // Scheduling
  getOrAddStripboard: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/stripboard`),
  updateStripboard: (projectId: string, documentId: string, stripboardId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/scheduling/stripboard/${stripboardId}`
    ),
  duplicateStripboard: (projectId: string, documentId: string, stripboardId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/scheduling/stripboard/${stripboardId}/duplicate`
    ),
  deleteStripboard: (projectId: string, documentId: string, stripboardId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/scheduling/stripboard/${stripboardId}`
    ),
  moveStripboardToBoneyard: (projectId: string, documentId: string, stripboardId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/scheduling/stripboard/${stripboardId}/archive`
    ),
  moveBoneyardToStripBoard: (projectId: string, documentId: string, stripboardId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/scheduling/stripboard/${stripboardId}/unarchive`
    ),
  getBoneyardData: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/boneyard`),
  getShootingLocations: (projectId: string, documentId: string) => {
    return path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/locations`);
  },
  exportSex: (projectId: string, documentId: string) => {
    return path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/export-sex`);
  },
  getShootDaySides: (projectId: string, documentId: string, daybreakId: number) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/sides/${daybreakId}`),
  exportShootDaySidesPdf: (projectId: string, documentId: string, daybreakId: number) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/sides/${daybreakId}/export-pdf`),
  exportDooDXlsx: (projectId: string, documentId: string) => {
    return path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/scheduling/day-out-of-days/export`
    );
  },
  reRunScheduling: (projectId: string, documentId: string) => {
    return path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/re-run`);
  },
  runScriptBreakdownAi: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/ai/script-breakdown`),
  runCharacterBreakdownAi: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/ai/character-breakdown`),
  runCoverageAi: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/documents/${documentId}/ai/coverage`),
  getDaysOutOfDays: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/day-out-of-days`),
  upsertDoodDayTypeOverride: (projectId: string, documentId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/scheduling/day-out-of-days/overrides`
    ),

  // Scheduling Daybreaks
  createAutomatedDaybreaks: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/daybreaks/automated`),
  createManualDaybreak: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/daybreaks/manual`),
  createBanner: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/banners`),
  updateOrDeleteBanner: (projectId: string, documentId: string, bannerId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/banners/${bannerId}`),
  updateOrDeleteDaybreak: (projectId: string, documentId: string, daybreakId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/scheduling/daybreaks/${daybreakId}`),

  // Teams Resources
  getSeatInvites: path(API_USER, "/invitations"),
  sendInvite: (projectId: string) => path(API_PROJECT, `/${projectId}/invite`),
  acceptInvitation: (invitationId: string) => path(API_USER, `/invitations/${invitationId}/accept`),
  declineInvitation: (invitationId: string) =>
    path(API_USER, `/invitations/${invitationId}/decline`),
  acceptInvitationByToken: path(API_USER, "/invitations/accept-by-token"),
  previewInvitationByToken: path(API_USER, "/invitations/preview-by-token"),

  // Budgeting
  getBudgetVersions: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/versions`),
  getBudgetTemplates: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/templates`),
  createBudget: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting`),
  getBudget: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}`),
  lockBudget: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/lock`),
  updateBudgetInfo: (projectId: string, documentId: string, budgetId: string, itemId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/items/${itemId}`
    ),
  updateBudgetSubCategory: (
    projectId: string,
    documentId: string,
    budgetId: string,
    subCategoryId: string
  ) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/sub-categories/${subCategoryId}`
    ),
  updateBugetCurrency: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/currency`),
  exportBudgetXlsx: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/export`),
  exportBudgetMmbx: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/export/mmbx`),
  importBudgetMmbx: (projectId: string, documentId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/import/mmbx`),
  getBudgetTaxIncentives: (projectId: string, documentId: string, budgetId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/tax-incentives`
    ),
  getTaxIncentiveCatalog: () => path(ROOT, "/tax-incentive-catalog"),
  getTaxIncentiveJurisdiction: (jurisdictionCode: string) =>
    path(ROOT, `/tax-incentive-catalog/${encodeURIComponent(jurisdictionCode)}`),
  getBudgetBenchmarks: () => path(ROOT, "/budget-benchmarks"),
  getBudgetBenchmarkMeta: () => path(ROOT, "/budget-benchmarks/meta"),
  getBudgetBenchmarksForProductionType: (productionType: string) =>
    path(ROOT, `/budget-benchmarks/for-production-type/${encodeURIComponent(productionType)}`),
  saveBudgetVersion: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/save-version`),

  reRunBudgeting: (projectId: string, documentId: string, budgetId: string) => {
    return path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/re-run`
    );
  },
  saveBudgetRerunParams: (projectId: string, documentId: string, budgetId: string) => {
    return path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/rerun-params`
    );
  },
  createBudgetItem: (
    projectId: string,
    documentId: string,
    budgetId: string,
    subCategoryId: string
  ) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/sub-categories/${subCategoryId}/items`
    ),
  deleteBudgetItem: (projectId: string, documentId: string, budgetId: string, itemId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/items/${itemId}`
    ),
  duplicateBudgetItem: (projectId: string, documentId: string, budgetId: string, itemId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/items/${itemId}/duplicate`
    ),
  reorderBudgetItems: (projectId: string, documentId: string, budgetId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/items/reorder`
    ),
  reorderBudgetCategories: (projectId: string, documentId: string, budgetId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/categories/reorder`
    ),
  reorderBudgetSubCategories: (projectId: string, documentId: string, budgetId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/sub-categories/reorder`
    ),
  deleteBudgetVersion: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}`),
  restoreBudgetVersion: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/restore`),
  budgetVersionDiff: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/diff`),
  budgetVersionDiffCategory: (
    projectId: string,
    documentId: string,
    budgetId: string,
    categoryName: string
  ) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/diff/categories/${encodeURIComponent(categoryName)}`
    ),
  createBudgetCategory: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/categories`),
  updateBudgetCategory: (
    projectId: string,
    documentId: string,
    budgetId: string,
    categoryId: string
  ) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/categories/${categoryId}`
    ),
  deleteBudgetCategory: (
    projectId: string,
    documentId: string,
    budgetId: string,
    categoryId: string
  ) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/categories/${categoryId}`
    ),
  createBudgetSubCategory: (
    projectId: string,
    documentId: string,
    budgetId: string,
    categoryId: string
  ) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/categories/${categoryId}/sub-categories`
    ),
  updateBudgetSubCategoryStructure: (
    projectId: string,
    documentId: string,
    budgetId: string,
    subCategoryId: string
  ) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/sub-categories/${subCategoryId}/structure`
    ),
  deleteBudgetSubCategory: (
    projectId: string,
    documentId: string,
    budgetId: string,
    subCategoryId: string
  ) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/sub-categories/${subCategoryId}`
    ),
  createBudgetFringe: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/fringes`),
  getBudgetFringes: (projectId: string, documentId: string, budgetId: string) =>
    path(API_PROJECT, `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/fringes`),
  updateBudgetFringe: (projectId: string, documentId: string, budgetId: string, fringeId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/fringes/${fringeId}`
    ),
  deleteBudgetFringe: (projectId: string, documentId: string, budgetId: string, fringeId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/budgeting/${budgetId}/fringes/${fringeId}`
    ),

  // Actor Management
  getActors: (projectId: string) => path(API_PROJECT, `/${projectId}/actors`),
  getActor: (projectId: string, actorId: string) =>
    path(API_PROJECT, `/${projectId}/actors/${actorId}`),
  assignActorToCharacter: (projectId: string, documentId: string, characterId: string) =>
    path(
      API_PROJECT,
      `/${projectId}/screenplays/${documentId}/character-breakdowns/characters/${characterId}/assign-actor`
    ),

  // Workspace Management
  workspaces: path(API_WORKSPACE, ""),
  getWorkspace: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}`),
  updateWorkspace: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}`),
  deleteWorkspace: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}`),

  // Workspace custom roles
  listWorkspaceCustomRoles: (workspaceId: string, includeArchived?: boolean) => {
    const base = path(API_WORKSPACE, `/${workspaceId}/roles`);
    if (includeArchived) {
      return `${base}?include_archived=true`;
    }
    return base;
  },
  createWorkspaceCustomRole: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}/roles`),
  updateWorkspaceCustomRole: (workspaceId: string, roleId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/roles/${roleId}`),

  // Workspace Members
  getWorkspaceMembers: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}/members`),
  getWorkspaceMember: (workspaceId: string, memberId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/members/${memberId}`),
  updateWorkspaceMember: (workspaceId: string, memberId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/members/${memberId}`),
  removeWorkspaceMember: (workspaceId: string, memberId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/members/${memberId}`),
  getWorkspaceMemberLoginActivity: (workspaceId: string, memberId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/members/${memberId}/login-activity`),
  getProjectMemberCandidates: (workspaceId: string, projectId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/projects/${projectId}/member-candidates`),
  removeMemberFromProject: (workspaceId: string, projectId: string, memberId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/projects/${projectId}/members/${memberId}`),

  // Login Activity
  getMyLoginActivity: path(API_USER, "/me/login-activity"),

  // Workspace Invitations
  inviteWorkspaceMember: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/invitations`),
  getWorkspaceInvitations: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/invitations`),
  updateWorkspaceInvitation: (workspaceId: string, invitationId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/invitations/${invitationId}`),
  cancelWorkspaceInvitation: (workspaceId: string, invitationId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/invitations/${invitationId}`),

  // Workspace Activity Logs
  getWorkspaceActivityLogs: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/activity-logs`),

  // Workspace dashboard metrics
  getWorkspaceDashboardMetrics: (workspaceId: string, days?: number) => {
    const base = path(API_WORKSPACE, `/${workspaceId}/dashboard/metrics`);
    if (days == null) return base;
    return `${base}?days=${days}`;
  },

  // Workspace AI credits
  getWorkspaceAiCreditsSummary: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/ai-credits/summary`),
  getWorkspaceAiCreditsActivity: (workspaceId: string, page?: number, take?: number) => {
    const base = path(API_WORKSPACE, `/${workspaceId}/ai-credits/activity`);
    const params = new URLSearchParams();
    if (page != null) params.set("page", String(page));
    if (take != null) params.set("take", String(take));
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  },

  // Workspace billing (Stripe customer is workspace-scoped; plan data follows workspace owner)
  getWorkspaceBillingSummary: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/billing/summary`),
  getWorkspaceBillingInvoices: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/billing/invoices`),
  createWorkspaceBillingPortal: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/billing/portal`),

  // Workspace scoped API keys
  listWorkspaceApiKeys: (workspaceId: string, view?: "mine" | "all") => {
    const base = path(API_WORKSPACE, `/${workspaceId}/api-keys`);
    if (view === "all") {
      return `${base}?view=all`;
    }
    return base;
  },
  createWorkspaceApiKey: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}/api-keys`),
  rotateWorkspaceApiKey: (workspaceId: string, keyId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/api-keys/${keyId}/rotate`),
  renameWorkspaceApiKey: (workspaceId: string, keyId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/api-keys/${keyId}`),

  getWorkspaceSso: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}/sso`),
  registerWorkspaceSso: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}/sso`),
  verifyWorkspaceSsoDomain: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/sso/verify-domain`),
  testWorkspaceSsoSignIn: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/sso/test-sign-in`),
  getWorkspaceSsoAffectedAccounts: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/sso/affected-accounts`),
  setWorkspaceSsoEnforcement: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/sso/enforcement`),
  enableWorkspaceSso: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}/sso/enable`),
  disableWorkspaceSso: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}/sso/disable`),
  deleteWorkspaceSso: (workspaceId: string) => path(API_WORKSPACE, `/${workspaceId}/sso`),
  revokeWorkspaceApiKey: (workspaceId: string, keyId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/api-keys/${keyId}`),
  getWorkspaceClaudeConnector: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/claude-connector`),
  createWorkspaceClaudeConnector: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/claude-connector`),
  deleteWorkspaceClaudeConnector: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/claude-connector`),
  getWorkspaceMcpConnector: (workspaceId: string, client: string) =>
    path(API_WORKSPACE, `/${workspaceId}/mcp-connectors/${encodeURIComponent(client)}`),
  createWorkspaceMcpConnector: (workspaceId: string, client: string) =>
    path(API_WORKSPACE, `/${workspaceId}/mcp-connectors/${encodeURIComponent(client)}`),
  deleteWorkspaceMcpConnector: (workspaceId: string, client: string) =>
    path(API_WORKSPACE, `/${workspaceId}/mcp-connectors/${encodeURIComponent(client)}`),
  /** OAuth consent for AI apps connecting over MCP. */
  describeMcpOAuthConsent: path(ROOT, "/mcp-oauth/consent/describe"),
  submitMcpOAuthConsent: path(ROOT, "/mcp-oauth/consent"),
  /** Connected AI apps (OAuth grants) in a workspace. */
  getWorkspaceMcpConnections: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/mcp-connections`),
  getAllWorkspaceMcpConnections: (workspaceId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/mcp-connections/all`),
  revokeWorkspaceMcpConnection: (workspaceId: string, grantId: string) =>
    path(API_WORKSPACE, `/${workspaceId}/mcp-connections/${encodeURIComponent(grantId)}`),

  // Note: Separate permission endpoints have been removed
  // Permissions are now included in the main project and workspace endpoints
  // This reduces API calls and improves performance

  // Referrals (purchase-triggered OTP program)
  referralsValidate: (code: string) =>
    path(ROOT, `/referrals/validate/${encodeURIComponent(code)}`),
  referralsPendingBonus: path(ROOT, "/referrals/pending-bonus"),
  referralsCode: path(ROOT, "/referrals/code"),
  referralsStats: path(ROOT, "/referrals/stats"),
  referralsHistory: path(ROOT, "/referrals/history"),
};
